import React, { ReactElement, useMemo } from "react";
import { ProgressTrackerCircleProps } from './ProgressTrackerCircle.types';

/**
 * Renders a circular green checkmark (✅).
 *
 * @param {number} size - The size of the checkmark.
 * @param {number} radius - The radius of the circle.
 * @returns {ReactElement} The rendered checkmark.
 */
function renderCheckmark(size: number, radius: number): ReactElement {
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle cx={size / 2}
                    cy={size / 2}
                    fill="#0bb883"
                    r={radius} />
            <polyline fill="none"
                      points={`${size * 0.38},${size * 0.52} ${size * 0.46},${size * 0.6} ${size * 0.63},${size * 0.42}`}
                      stroke="#fff"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"/>
        </svg>
    );
}

/**
 * Renders a circular progress tracker.
 *
 * @param {ProgressTrackerCircleProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function ProgressTrackerCircle({
    currentValue,
    maxValue,
    color           = "#224498",
    size            = 100,
    thickness       = 10,
}: ProgressTrackerCircleProps): ReactElement | null {
    const radius = useMemo(() => (size - thickness) / 2, [size, thickness]);
    const circumference = useMemo(() => 2 * Math.PI * radius, [radius]);
    const progress = useMemo(
        () => Math.min(currentValue / maxValue, 1),
        [currentValue, maxValue]
    );
    const strokeDashoffset = useMemo(
        () => circumference - progress * circumference,
        [circumference, progress]
    );

    if (currentValue >= maxValue) {
        return renderCheckmark(size, radius);
    }

    return (
        <div className="ProgressTrackerCircle">
            <svg width={size} height={size}>
                <circle cx={size / 2}
                        cy={size / 2}
                        fill="none"
                        r={radius}
                        stroke={color + "33"}
                        strokeWidth={thickness} />
                <circle cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        stroke={color}
                        strokeWidth={thickness}
                        fill="none"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
                <text dominantBaseline="central"
                      fill="#22234A"
                      fontFamily="Eloquia, sans-serif"
                      fontSize={size * 0.25}
                      fontWeight="550"
                      textAnchor="middle"
                      x="50%"
                      y="50%">
                    {`${currentValue}/${maxValue}`}
                </text>
            </svg>
        </div>
    );
}

export default ProgressTrackerCircle;

